<template>
  <div class="container col-md-8 p-4 mt-md-5">
    <!-- Step 1: Set Game Settings -->
    <div v-if="step === 'gamesettings'" class="row">
      <h2 class="text-uppercase">{{ $t('teamSetup.gameSettings') }}</h2>
      <div class="col-12 my-2 d-flex justify-content-between align-items-center">
        <label>{{ $t('teamSetup.teamName') }}:</label>
        <select class="p-1" v-if="gameSettings.type === 'tournament'" v-model="gameSettings.teamAName">
          <option value="" disabled>{{ $t('teamSetup.selectTeam') }}</option>
          <option v-for="team in tournamentTeams" :key="team.id" :value="team.name">{{ team.name }}</option>
        </select>
        <input v-else class="p-1" v-model="gameSettings.teamAName" :placeholder="$t('teamSetup.enterTeamName')
          " />
      </div>
      <div class="col-12 my-2 d-flex justify-content-between align-items-center">
        <label>Team Name:</label>
        <select class="p-1" v-if="gameSettings.type === 'tournament'" v-model="gameSettings.teamBName">
          <option value="" disabled>{{ $t('teamSetup.selectTeam') }}</option>
          <option v-for="team in tournamentTeams" :key="team.id" :value="team.name">{{ team.name }}</option>
        </select>
        <input v-else class="p-1" v-model="gameSettings.teamBName" :placeholder="$t('teamSetup.enterTeamName')
          " />
      </div>
      <div class="col-12 my-2 d-md-flex justify-content-between align-items-center"
        v-show="gameSettings.teamAName && gameSettings.teamBName">
        <label class="d-block d-md-inline">{{ $t('teamSetup.selectHomeTeam') }}:</label>
        <div class="btn-group" role="group" aria-label="Home Team Selection">
          <input type="radio" class="btn-check" id="teamA" v-model="gameSettings.homeTeam"
            :value="gameSettings.teamAName">
          <label class="btn"
            :class="{ 'btn-outline-bb5': true, 'active': gameSettings.homeTeam === gameSettings.teamAName }"
            for="teamA">{{ gameSettings.teamAName }}</label>

          <input type="radio" class="btn-check" id="teamB" v-model="gameSettings.homeTeam"
            :value="gameSettings.teamBName">
          <label class="btn" for="teamB"
            :class="{ 'btn-outline-bb5': true, 'active': gameSettings.homeTeam === gameSettings.teamBName }">{{
              gameSettings.teamBName }}</label>
        </div>
      </div>
      <div class="col-12 my-2 d-flex justify-content-between align-items-center" v-if="gameSettings.type === 'single'">
        <label>{{ $t('teamSetup.enforceGenderLimit') }} :</label>
        <div class="btn-group" role="group" aria-label="Enforce Gender Limit">
          <input type="radio" class="btn-check" id="enforceYes" v-model="gameSettings.enforceGenderLimit" value="1">
          <label class="btn" :class="{ 'btn-outline-bb5': true, 'active': gameSettings.enforceGenderLimit === '1' }"
            for="enforceYes">{{ $t('yes') }}</label>

          <input type="radio" class="btn-check" id="enforceNo" v-model="gameSettings.enforceGenderLimit" value="0">
          <label class="btn" :class="{ 'btn-outline-bb5': true, 'active': gameSettings.enforceGenderLimit === '0' }"
            for="enforceNo">{{ $t('no') }}</label>
        </div>
      </div>
    </div>

    <!-- Step 2: Add Players to Teams -->
    <div v-if="step === 'player'" class="row mb-3">
      <h2 class="text-uppercase col-12">{{ $t('teamSetup.lineUp') }}</h2>
      <div class="col-12 btn-group" role="group" aria-label="Team Selection">
        <template v-for="(team, index) in orderedTeams" :key="team.id">
          <input type="radio" class="btn-check" :id="team.name" v-model="selectedTeam" :value="index">
          <label class="btn" :class="{ 'btn-outline-bb5': true, 'active': selectedTeam === index }" :for="team.name">
            {{ team.name }}
          </label>
        </template>
      </div>
    </div>
    <div v-if="step === 'player'" class="row">
      <div v-if="selectedTeamData" class="col-12">

        <!-- Starters -->
        <div v-if="selectedTeamData.players.length > 0" class="row column-gap-1 mb-4">
          <div class="col-12 gy-2">
            <div class="row g-0 column-gap-1 align-items-center">
              <h3 class="col text-uppercase m-0">{{ $t('teamSetup.starters') }}</h3>
              <div class="col-1 p-0 text-center">{{ $t('player.mf') }}</div>
              <div class="col-1 p-0 text-center">{{ $t('player.numbershort') }}</div>
              <div v-if="showDelete" class="col-1 p-0 text-center"></div>
            </div>
          </div>
          <div v-for="(player, index) in selectedTeamData.players.slice(0, 5)" :key="player.id" class="col-12 gy-2">
            <div class="row g-0 column-gap-1 align-items-center">
              <div class="col g-0 column-gap-1 row" @click="showModal(player, index + 1)">
                <div class="col-1 bg-bb5-yellow py-2 text-center">{{ index + 1 }}</div>
                <div class="col bg-bb5-yellow px-1 py-2">{{ shortName(player.name) }}</div>
                <div class="col-1 bg-bb5-yellow py-2 text-uppercase text-center">{{ player.gender }}</div>
                <div class="col-1 bg-bb5-yellow py-2 text-center">{{ player.number }}</div>
              </div>
              <button v-if="showDelete" class="col-1 btn-danger m-0 py-2"
                @click="delPlayer(selectedTeamData.id, player.id)">X</button>
            </div>
          </div>
        </div>

        <!-- Substitutes -->
        <div v-if="selectedTeamData.players.length > 5" class="row column-gap-1 mb-4">
          <h3 class="col-8 text-uppercase m-0">{{ $t('teamSetup.substitutes') }}</h3>
          <div v-for="(player, index) in selectedTeamData.players.slice(5)" :key="player.id" class="col-12 gy-2">
            <div class="row g-0 column-gap-1 align-items-center">
              <div class="col g-0 column-gap-1 row" @click="showModal(player, index + 6)">
                <div class="col-1 bg-bb5-yellow py-2 text-center">{{ index + 6 }}</div>
                <div class="col bg-bb5-yellow px-1 py-2">{{ shortName(player.name) }}</div>
                <div class="col-1 bg-bb5-yellow py-2 text-uppercase text-center">{{ player.gender }}</div>
                <div class="col-1 bg-bb5-yellow py-2 text-center">{{ player.number }}</div>
              </div>
              <button v-if="showDelete" class="col-1 btn-danger m-0 py-2"
                @click="delPlayer(selectedTeamData.id, player.id)">X</button>
            </div>
          </div>
        </div>
        <!-- Add Player Section (same as before) -->
        <div v-if="selectedTeamData.players.length < 8 && showAddNewPlayer" class="row column-gap-1 g-0">
          <h3 class="col-12">{{ $t('teamSetup.addPlayer') }}</h3>
          <input v-model="newPlayers[selectedTeamData.id].name" :placeholder="$t('teamSetup.playerName')"
            class="col-6 p-1" />
          <select v-model="newPlayers[selectedTeamData.id].gender" class="col-2">
            <option value="M">M</option>
            <option value="F">F</option>
          </select>
          <input v-model.number="newPlayers[selectedTeamData.id].number" :placeholder="$t('player.numbershort')"
            maxlength="3" class="col-2 p-1" />
          <button @click="addPlayerToTeam(selectedTeamData.id)" class="col btn btn-bb5-black m-0">+</button>
        </div>
        <div v-if="selectedTeamData.players.length < 8 && showAddPlayer" class="row column-gap-1 g-0">
          <h3 class="col-12">{{ $t('teamSetup.addPlayer') }}</h3>
          <select v-model="selectedPlayer" id="player-select" class="col-10">
            <option value="" disabled>{{ $t('teamSetup.selectPlayer') }}</option>
            <option v-for="player in players" :key="player.id" :value="player.id">
              {{ player.name }}
            </option>
          </select>

          <!-- Add Button -->
          <button @click="addPlayerfromApiToTeam(selectedTeamData.id)" :disabled="!selectedPlayer"
            class="col btn btn-bb5-black m-0">+</button>
        </div>
      </div>
    </div>
    <div v-if="step === 'officials'" class="row">
        <Officials />
      </div>
    <!-- Start Game Button -->
    <div class="row my-2">
      <div class="col-7">
        <button @click="gotoGameSettings" class="btn btn-bb5-black m-0 me-2 p-2"
          v-if="step !== 'gamesettings'">{{
            $t('teamSetup.gameSettings') }}</button>
        <button @click="addPlyers" class="btn btn-bb5-black m-0 me-2 p-2" :disabled="!canAddPlayer" v-if="step !== 'player'">{{
          $t('teamSetup.players') }}</button>
        <button @click="setOfficals" class="btn btn-bb5-black m-0 p-2" v-if="step !== 'officials'">{{ $t('teamSetup.officials') }}</button>
      </div>
      <div class="col-5 d-grid">
        <button @click="startGame" class="btn btn-bb5-yellow m-0 p-2" :disabled="!canStartGame">{{
          $t('teamSetup.startGame')
          }}</button>
      </div>
    </div>
    <LineupPositionModal :current-position="currentPosition" :max-positions="8" @positionchanged="updatePlayerPosition"
      ref="lineupPositionModal" />
  </div>
</template>


<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import apiService from "@/services/apiService";
import localStorageService from "@/services/localStorageService";
import { savePlayer, loadAllPlayers } from '@/services/indexedDB';
import core from "@/mixins/Core";
import rules from '@/mixins/Rules';
import LineupPositionModal from "@/components/Modal/LineupPositionModal.vue";
import Officials from "@/components/TeamSetup/Officials.vue"; 
export default {
  components: {LineupPositionModal, Officials},
  data() {
    return {
      isModalVisible: false,
      currentPosition: 0,// Track the index of the player being dragged
      hoveringIndex: null,       // Track the index where the player will be dropped
      step: 'gamesettings',
      selectedTeam: 0,
      newPlayers: {},
      selectedPlayer: null, // ID of the currently selected player
      players: [], // All players fetched from API
      tournamentTeams: [],
      showDelete: true,
      showAddNewPlayer: true,
      showAddPlayer: true,
      showOfficials: false,
    };
  },
  mounted() {
    // Fetch all players from the API when the component is mounted
    if(this.gameSettings.type === 'tournament') {
      this.fetchTeams()
      this.showAddPlayer = false;
      this.showAddNewPlayer = false;
      this.showDelete = false;
    } else {
      this.fetchPlayers();
    }
  },
  mixins: [core, rules],
  computed: {
    ...mapState(['teams', 'game']),
    ...mapGetters(['gameSettings', 'gameIsRunning']),
    orderedTeams() {
      return this.teams.slice().sort((a, b) => {
        if (a.name === this.gameSettings.homeTeam) return -1;
        if (b.name === this.gameSettings.homeTeam) return 1;
        return 0;
      });
    },
    selectedTeamData() {
      return this.orderedTeams[this.selectedTeam];
    },
    canStartGame() {
      return this.teams.every(team => team.players.length >= 5);
    },
    canAddPlayer() {
      return (
          this.gameSettings.teamAName &&
          this.gameSettings.teamBName &&
          this.gameSettings.homeTeam
      );
    },
  },
  methods: {
    ...mapActions(['setGameSettings', 'addPlayer', 'deletePlayer', 'setGameIsRunning', 'setGame' , 'resetStats', "gameView", "setGameView"]),
    async fetchPlayers() {
      try {
        if(localStorageService.get('useApi') === true) {
          const response = await apiService.get('players');
          this.players = response.data;
        } else {
          this.players = await loadAllPlayers();
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    },
    async fetchTeamPlayers(teamId) {
      try {
        const response = await apiService.get('players/team/' + teamId);
        let players = response.data;

        // Get the index of the team based on teamId
        let teamIndex = this.teams.findIndex(t => t.id === teamId);

        // Check if the team exists in the list
        if (teamIndex === -1) {
          console.error('Team not found!');
          return;
        }

        // Get the current players for that team
        let existingPlayers = this.teams[teamIndex].players || [];

        for (var index in players) {
          let player = players[index];

          // Check if the player already exists in the team
          let playerExists = existingPlayers.some(p => p.id === player.id);

          // Add the player if they don't already exist
          if (!playerExists) {
            this.addPlayer({
              teamIndex: teamIndex,
              player: {
                id: player.id,
                name: player.name,
                number: player.number,
                gender: player.gender,
              },
            });
          }
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    },
    async fetchTeams() {
      try {
        const response = await apiService.get('tournament/1');
        this.tournamentTeams = response.data.teams;
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    },
    addPlyers() {
      if (!this.gameSettings.teamAName || !this.gameSettings.teamBName) {
        alert('Please set names for both teams.');
        return;
      }

      if (!this.gameSettings.homeTeam) {
        alert('Please select the home team.');
        return;
      }

      // Speichere die
      this.setGameSettings(this.gameSettings);
      if(this.gameSettings.type === 'tournament') {
        for( let teamArr in this.teams) {
          const teamData = this.tournamentTeams.find(t => t.name === this.teams[teamArr].name);
          this.teams[teamArr].id = teamData.id
          this.teams[teamArr].teamSide = this.gameSettings.homeTeam === this.teams[teamArr].name ? 'home' : 'guest'
          this.fetchTeamPlayers(teamData.id)
        }
      }
      // Initialisiere das Formular für die neuen Spieler
      this.newPlayers = this.teams.reduce((acc, team) => {
        acc[team.id] = { name: '', number: '', gender: 'M' };
        return acc;
      }, {});

      this.step = 'player';

    },
    gotoGameSettings(){
      this.step = 'gamesettings';
    },
    addPlayerfromApiToTeam (teamId) {
      const team = this.teams.find(t => t.id === teamId);
      // Find the player by the selected player ID
      const playerIndex = this.players.findIndex(p => p.id === this.selectedPlayer);
      const player = this.players.find(p => p.id === this.selectedPlayer)
      if (!team) {
        alert('Invalid team.');
        return;
      }
      if (team.players.length < 8) {
          this.addPlayer({
            teamIndex: this.teams.findIndex(t => t.id === teamId),
          player: {
            id: player.id,
            name: player.name,
            number: player.number,
            gender: player.gender,
          },
        });
        this.players.splice(playerIndex, 1);

        // Optionally, reset the selectedPlayer to null or an empty string
        this.selectedPlayer = null;
      }
    },
    addPlayerToTeam(teamId) {
      const newPlayer = this.newPlayers[teamId];
      if (!newPlayer || !newPlayer.name || !newPlayer.number || !newPlayer.gender) {
        alert('All fields are required: name, number, and gender.');
        return;
      }

      const team = this.teams.find(t => t.id === teamId);
      if (!team) {
        alert('Invalid team.');
        return;
      }

      const isNumberUnique = !team.players.some(
          (player) => player.number === newPlayer.number
      );

      if (!isNumberUnique) {
        alert('Player number must be unique within the team.');
        return;
      }

      const genderCount = team.players.filter(
          (player) => player.gender === newPlayer.gender
      ).length;

      if (
          this.gameSettings.enforceGenderLimit === '1' &&
          genderCount >= this.gameSettings.maxPlayersPerGender
      ) {
        alert(`Cannot add more than ${this.gameSettings.maxPlayersPerGender} ${newPlayer.gender} players.`);
        return;
      }

      if (team.players.length < 8) {
        const id = Date.now()
        this.addPlayer({
          teamIndex: this.teams.findIndex(t => t.id === teamId),
          player: {
            id: id,
            name: newPlayer.name,
            number: newPlayer.number,
            gender: newPlayer.gender,
          },
        });
        savePlayer({
          id: id,
          name: newPlayer.name,
          number: newPlayer.number,
          gender: newPlayer.gender,
        })
        // Reset the form fields for the added player
        this.newPlayers[teamId] = { name: '', number: '', gender: 'M' };
      }
    },
    delPlayer(teamId, playerId) {
      const teamIndex = this.teams.findIndex(t => t.id === teamId);
      const playerIndex = this.teams[teamIndex].players.findIndex(p => p.id === playerId);
      if (teamIndex === -1 || playerIndex === -1) {
        alert('Invalid team or player.');
        return;
      }
      this.deletePlayer({ teamIndex, playerId });
    },
    genderLimitReached(team, gender) {
      return (
          this.gameSettings.enforceGenderLimit === '1' &&
          team.players.filter((player) => player.gender === gender).length >=
          this.gameSettings.maxPlayersPerGender
      );
    },
    showModal(player, position) {
      this.selectedPlayer = player; // Spieler zur Bearbeitung speichern
      this.currentPosition = position
      this.$refs.lineupPositionModal.openModal();
    },
    updatePlayerPosition(newPosition) {
      if (this.selectedPlayer) {
        // Zugriff auf die Playerliste
        const playerList = this.selectedTeamData.players;

        // Aktuelle Position des Spielers bestimmen
        const currentIndex = playerList.findIndex(
          (player) => player.id === this.selectedPlayer.id
        );

        if (currentIndex !== -1) {
          // Berechne das Ziel der neuen Position (0-basiert)
          const targetIndex = newPosition - 1;

          if (targetIndex >= 0 && targetIndex < playerList.length) {
            // Spieler an der Zielposition erhalten
            const targetPlayer = playerList[targetIndex];

            // Tausche die Position von `selectedPlayer` und `targetPlayer`
            playerList[currentIndex] = targetPlayer;
            playerList[targetIndex] = this.selectedPlayer;

            console.log(
              `Spieler ${this.selectedPlayer.name} (von Position ${
                currentIndex + 1
              }) hat die Position mit Spieler ${
                targetPlayer.name
              } (Position ${newPosition}) getauscht.`
            );
          } else {
            console.error(
              "Die neue Position liegt außerhalb des gültigen Bereichs!"
            );
          }
        } else {
          console.error("Spieler nicht in der Liste gefunden!");
        }
      }
    },
    async startGame() {
      await this.resetStats()
      const homeTeam = this.orderedTeams[0];
      const guestTeam = this.orderedTeams[1];

      const homeTeamValid = this.checkGenderLimit(homeTeam.id);
      if (!homeTeamValid) {
        console.error("Das Heimteam erfüllt das Geschlechterlimit nicht.");
        return false; // Validierung fehlgeschlagen
      }

      // Überprüfe das Geschlechterlimit für das Gästeteam
      const guestTeamValid = this.checkGenderLimit(guestTeam.id);
      if (!guestTeamValid) {
        console.error("Das Gästeteam erfüllt das Geschlechterlimit nicht.");
        return false; // Validierung fehlgeschlagen
      }


      const gameData = {
        id: Date.now(),
        outs: 0,
        homeTeam: homeTeam.name,
        guestTeam: guestTeam.name,
        offenceTeam: 'guest',
        inning: 1,
        home: {
          team: homeTeam.name,
          score: 0,
          players: homeTeam.players.map((player, index) => ({
            id: player.id,
            curPos: 0,
            substitutWithId: 0,
            lineUpPosition: index + 1,
          })),
          lineUpPosition: 1
        },
        guest: {
          team: guestTeam.name,
          score: 0,
          players: guestTeam.players.map((player, index) => ({
            id: player.id,
            curPos: 0,
            substitutWithId: 0,
            lineUpPosition: index + 1,
          })),
          lineUpPosition: 1,
        },
      };
      // Setze das Spiel und starte es
      this.setGameView('field');
      this.setGame(gameData);
      this.setGameIsRunning(true);
      localStorage.setItem('currentgame', gameData.id);
      this.$router.push('/game');
    },
    endGame() {
      //this.setGameIsRunning(false); // End the game
      alert('Ball Game!');
    },
    setOfficals() {
      this.step = 'officials';
    },
  }
};
</script>




<style scoped>
button {
  margin-left: 10px;
  background-color: #ff4d4d;
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
}

button:hover {
  background-color: #ff1a1a;
}

</style>
